/**
 * ss-age-gate.js — Refined age verification
 * - Instant unlock for returning visitors (no scroll freeze)
 * - Focus trap while open; restores focus on close
 * - Scroll lock only while gate is visible; restores scroll position
 * - Dispatches ss:age-verified so hero autoplay can resume safely
 */
(function () {
  'use strict';

  var KEY = 'ss_age_verified';
  var SESSION_OK = 'ss_age_session_ok';
  var FAIL = 'ss_age_blocked';
  var scrollY = 0;
  var lastFocus = null;

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    var secure = location.protocol === 'https:' ? ';Secure' : '';
    document.cookie = name + '=' + value + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax' + secure;
  }

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  function isVerified() {
    try {
      if (sessionStorage.getItem(SESSION_OK) === '1') return true;
      if (localStorage.getItem(KEY) === '1') return true;
    } catch (e) {}
    return getCookie(KEY) === '1';
  }

  function isBlocked() {
    try {
      if (sessionStorage.getItem(FAIL) === '1') return true;
    } catch (e) {}
    return getCookie(FAIL) === '1';
  }

  function clearScrollLocks() {
    var html = document.documentElement;
    var body = document.body;
    html.classList.remove('ss-age-locked', 'ss-age-pending');
    html.style.removeProperty('overflow');
    if (body) {
      body.classList.remove('ss-age-locked');
      body.style.removeProperty('overflow');
      body.style.removeProperty('position');
      body.style.removeProperty('top');
      body.style.removeProperty('width');
      body.style.removeProperty('touch-action');
      body.style.removeProperty('padding-right');
    }
  }

  function markVerified() {
    try {
      sessionStorage.setItem(SESSION_OK, '1');
      localStorage.setItem(KEY, '1');
    } catch (e) {}
    setCookie(KEY, '1', 30);
    document.documentElement.classList.remove('ss-age-pending', 'ss-age-locked');
    document.documentElement.classList.add('ss-age-verified');
    clearScrollLocks();
  }

  function restoreScroll() {
    try {
      window.scrollTo(0, scrollY || 0);
    } catch (e) {}
  }

  function lockScroll() {
    scrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add('ss-age-locked', 'ss-age-pending');
    document.documentElement.classList.remove('ss-age-verified');
    if (document.body) {
      document.body.classList.add('ss-age-locked');
    }
  }

  function focusables(root) {
    return Array.prototype.slice.call(
      root.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function init() {
    var gate = document.getElementById('ss-age-gate');
    if (!gate) {
      document.documentElement.classList.remove('ss-age-pending', 'ss-age-locked');
      document.documentElement.classList.add('ss-age-verified');
      clearScrollLocks();
      return;
    }

    if (gate.parentElement !== document.body) {
      document.body.insertBefore(gate, document.body.firstChild);
    }

    var DAYS = parseInt(gate.getAttribute('data-cookie-days'), 10) || 30;
    var panel = gate.querySelector('[data-ss-age-panel]');
    var blocked = gate.querySelector('[data-ss-age-blocked]');
    var yesBtn = gate.querySelector('[data-ss-age-yes]');
    var noBtn = gate.querySelector('[data-ss-age-no]');

    function unlock() {
      gate.classList.remove('is-visible', 'is-leaving');
      gate.setAttribute('hidden', '');
      gate.setAttribute('aria-hidden', 'true');
      gate.style.cssText = 'display:none!important;visibility:hidden!important;opacity:0!important;pointer-events:none!important;';
      markVerified();
      restoreScroll();
      if (lastFocus && typeof lastFocus.focus === 'function') {
        try { lastFocus.focus(); } catch (e) {}
      }
      try {
        document.dispatchEvent(new CustomEvent('ss:age-verified', { bubbles: true }));
      } catch (e) {}
      if (typeof window.ssClearScrollLocks === 'function') {
        window.ssClearScrollLocks();
      }
    }

    function lock() {
      if (document.documentElement.classList.contains('ss-age-verified')) return;
      lastFocus = document.activeElement;
      gate.style.cssText = '';
      gate.removeAttribute('hidden');
      gate.classList.add('is-visible');
      gate.setAttribute('aria-hidden', 'false');
      lockScroll();
      var focusTarget = yesBtn || (panel && panel.querySelector('button'));
      if (focusTarget) {
        setTimeout(function () {
          try { focusTarget.focus(); } catch (e) {}
        }, 40);
      }
    }

    function showBlocked() {
      if (panel) panel.hidden = true;
      if (blocked) blocked.hidden = false;
      lock();
    }

    function pass() {
      try {
        sessionStorage.setItem(SESSION_OK, '1');
        localStorage.setItem(KEY, '1');
      } catch (e) {}
      setCookie(KEY, '1', DAYS);
      document.documentElement.classList.add('ss-age-verified');
      document.documentElement.classList.remove('ss-age-pending', 'ss-age-locked');
      clearScrollLocks();
      if (typeof window.ssSyncScrollLock === 'function') window.ssSyncScrollLock();
      if (typeof window.ssForceReleaseScrollLock === 'function') window.ssForceReleaseScrollLock();
      gate.classList.add('is-leaving');
      setTimeout(unlock, 140);
    }

    function fail() {
      try { sessionStorage.setItem(FAIL, '1'); } catch (e) {}
      setCookie(FAIL, '1', 1);
      showBlocked();
    }

    if (window.Shopify && window.Shopify.designMode) {
      unlock();
      return;
    }

    if (isVerified()) {
      unlock();
      return;
    }

    if (isBlocked()) {
      showBlocked();
    } else {
      if (panel) panel.hidden = false;
      if (blocked) blocked.hidden = true;
      lock();
    }

    if (yesBtn) yesBtn.addEventListener('click', pass);
    if (noBtn) noBtn.addEventListener('click', fail);

    document.addEventListener('keydown', function (e) {
      if (gate.hasAttribute('hidden') || !gate.classList.contains('is-visible')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        return;
      }
      if (e.key !== 'Tab') return;
      var items = focusables(gate);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
